// ──────────────────────────────────────────────────────────────────────────
// Shared, null-safe formatters for tender / request card + preview.
//
// Why this file exists: three separate card components (Agent inline,
// TenderCard, StaffTenderCard) each reimplemented their own version of
// "show min - max age", "show salary range", "format position" — and
// each had the same null-handling bug producing strings like
//   "23 - null"      (age limit with missing max)
//   "$2,222 - null"  (salary with missing upper bound)
//   "-"              (empty description filler shown to the user)
//
// These helpers return `null` for fields that should be hidden entirely
// (caller is expected to `shouldRender(value)` before rendering the row),
// and they return polished strings like "From 23 years" / "Up to 30 years"
// when only one bound is given.
//
// All exports are pure functions — no React, no side effects — so they
// can be unit-tested in isolation and used both on the card and inside
// the preview modal without divergence.
// ──────────────────────────────────────────────────────────────────────────

import { formatCurrencyWithSymbol } from "./currencyFormatter";

const EMPTY_PLACEHOLDERS = new Set([
  "-",
  "—",
  "N/A",
  "n/a",
  "None",
  "null",
  "Not specified",
  "No description available",
]);

// True if the value carries no useful information and the row should
// stay collapsed instead of rendering a blank/empty/garbage label.
export function shouldRender(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "number") return true; // 0 is a valid value
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return false;
    if (EMPTY_PLACEHOLDERS.has(t)) return false;
    return true;
  }
  if (typeof value === "boolean") return true;
  // Arrays + objects: treat as non-empty if length/keys > 0.
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return Boolean(value);
}

// Return a single-value string or null.  Use for any scalar text field
// that should disappear from the UI when missing rather than rendering
// "-" or the literal "null".
export function formatValue(value) {
  return shouldRender(value) ? String(value).trim() : null;
}

// Title-case a raw enum/display value so it reads cleanly in the UI:
//   "professional" -> "Professional", "free_agent" -> "Free Agent",
//   "loan" -> "Loan". Returns null when there is nothing to show.
export function prettyValue(value) {
  if (!shouldRender(value)) return null;
  return String(value)
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Generic numeric range (e.g. age 23-30).  Null-safe on either bound:
//   formatRange(23, 30,   "years")  → "23 - 30 years"
//   formatRange(23, null, "years")  → "From 23 years"
//   formatRange(null, 30, "years")  → "Up to 30 years"
//   formatRange(null, null, "years") → null   (row should be hidden)
export function formatRange(min, max, unit = "") {
  const hasMin = shouldRender(min);
  const hasMax = shouldRender(max);
  if (!hasMin && !hasMax) return null;
  const suffix = unit ? ` ${unit}` : "";
  if (hasMin && hasMax) return `${min} - ${max}${suffix}`;
  if (hasMin) return `From ${min}${suffix}`;
  return `Up to ${max}${suffix}`;
}

// Currency range, same rules as formatRange but with currency formatting
// on each bound.  Used for both market value range and salary range.
export function formatCurrencyRange(min, max, currency = "USD") {
  const hasMin = shouldRender(min);
  const hasMax = shouldRender(max);
  if (!hasMin && !hasMax) return null;
  const fmt = (n) => formatCurrencyWithSymbol(n, currency);
  if (hasMin && hasMax) return `${fmt(min)} - ${fmt(max)}`;
  if (hasMin) return `From ${fmt(min)}`;
  return `Up to ${fmt(max)}`;
}

// Format the snake_case POSITION_CHOICES code into a human label.
// "center_back" → "Center-Back" (kept hyphen heuristics out — capitalise
// per word, callers wanting "Center-Back" specifically can override).
export function formatPositionLabel(positionCode) {
  if (!shouldRender(positionCode)) return null;
  return positionCode
    .toString()
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// The level a request recruits for — the club's own Youth / Professional /
// Women choice, stored as `Tender.recruiting_for`
// (dashboard/models.py:284, RECRUITING_FOR_CHOICES at :162-166) and served on
// every tender payload (dashboard/serializers.py:155).
//
// All THREE values are named honestly. The agent side deliberately knows only
// two (constants/agentTenderChoices.js: "`women` is intentionally NOT offered
// on either axis") and folds anything non-youth into "Professional", which on
// a CLUB request would print the wrong word for a women's request — the club
// form offers the third choice, so the club surface must be able to say it.
//
// An unset value returns null and the caller draws NOTHING. "Professional" is
// never assumed: a legacy request that predates the field did not pick it.
const RECRUITING_LEVEL_LABELS = {
  professional: "Professional",
  youth: "Youth",
  women: "Women",
};

export function formatRecruitingLevel(recruitingFor) {
  if (!shouldRender(recruitingFor)) return null;
  return RECRUITING_LEVEL_LABELS[String(recruitingFor).toLowerCase()] || null;
}

// The pill the level is drawn in. Same two-tone treatment the agent request
// card already uses for this very field (AgentTenderCard.js:44-47), so one
// field looks like one field wherever it appears.
export function recruitingLevelPillClass(recruitingFor) {
  return String(recruitingFor).toLowerCase() === "professional"
    ? "bg-[#F1F5F9] text-[#475569]"
    : "bg-[#E8F1FF] text-[#1B6FE0]";
}

// Who at the CLUB wrote this request, as `{ name, org }`, or null.
//
// `Tender.created_by` (dashboard/models.py:311, an FK to UserProfile) has
// always travelled on the tender payload — TenderBaseSerializer declares
// `created_by = CreatedBySerializer(read_only=True)` and lists it in
// Meta.fields, and TenderClubDetailSerializer inherits both. It was simply
// never rendered; the surface only knew `agent_attribution`, which answers the
// OTHER question ("an agency posted this for us", DECISION 009).
//
// The EMAIL that CreatedBySerializer also sends is deliberately not returned.
// Several club members read this page and the question is "who posted it", for
// which a name is the whole answer; an address here would publish a colleague's
// contact to everyone who opens the request.
//
// `name` first because that is the field the serializer fills; first + last are
// the fallback for a profile that never set the display name. No name means
// null means no line — never an invented author.
export function postedByMemberOf(tender) {
  const creator = tender?.created_by;
  if (!creator) return null;
  const name =
    (creator.name && String(creator.name).trim()) ||
    [creator.first_name, creator.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
  if (!name) return null;
  return { name, org: tender?.club?.name || null };
}

// Foot code → readable.
export function formatFoot(foot) {
  if (foot === "R") return "Right";
  if (foot === "L") return "Left";
  if (foot === "A") return "Any";
  return null;
}

// Height in cm with a unit suffix, null if missing or non-positive.
export function formatHeight(cm) {
  if (!shouldRender(cm)) return null;
  const n = Number(cm);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n} cm`;
}

// Salary line for the card / modal.  Returns null when the tender has
// no salary info at all so the row stays hidden.
//   tender.salary_type === "fixed_amount"   → fix_net_salary or fix_gross_salary
//   tender.salary_type === "salary_between" → range of (net) or (gross)
export function formatSalary(tender) {
  if (!tender) return null;
  const currency = tender.currency || "USD";
  if (tender.salary_type === "fixed_amount") {
    if (shouldRender(tender.fix_net_salary)) {
      return formatCurrencyWithSymbol(tender.fix_net_salary, currency);
    }
    if (shouldRender(tender.fix_gross_salary)) {
      return formatCurrencyWithSymbol(tender.fix_gross_salary, currency);
    }
    return null;
  }
  if (tender.salary_type === "salary_between") {
    const net = formatCurrencyRange(
      tender.net_salary_from,
      tender.net_salary_to,
      currency,
    );
    if (net) return net;
    return formatCurrencyRange(
      tender.gross_salary_from,
      tender.gross_salary_to,
      currency,
    );
  }
  return null;
}

// "Net" / "Gross" label so the salary row knows what kind of value it is
// showing.  Mirrors formatSalary's branch logic so labels never lie.
export function getSalaryKind(tender) {
  if (!tender) return null;
  if (tender.salary_type === "fixed_amount") {
    if (shouldRender(tender.fix_net_salary)) return "Net";
    if (shouldRender(tender.fix_gross_salary)) return "Gross";
    return null;
  }
  if (tender.salary_type === "salary_between") {
    if (
      shouldRender(tender.net_salary_from) ||
      shouldRender(tender.net_salary_to)
    ) {
      return "Net";
    }
    if (
      shouldRender(tender.gross_salary_from) ||
      shouldRender(tender.gross_salary_to)
    ) {
      return "Gross";
    }
  }
  return null;
}

// Market value / Transfer fee / Loan fee for the highlighted "fee" row
// near the top of the card.  Branches on type_of_tender:
//   buy   → maximum_transfer_fee or "Free transfer"
//   loan  → loan_fee_amount (when has_loan_fee)
//   other → market value (range or fixed, or "Not important")
// Returns { label, value } — both are non-null strings or both null.
// Loan-fee display for a loan tender — TWO states only (per Marin):
//   • a real loan fee amount present (loan_fee_amount set / > 0) → the amount
//   • otherwise (has_loan_fee false, OR true but no amount)      → "Free"
// The serializer exposes has_loan_fee + loan_fee_amount (NOT loan_fee).
export function formatLoanFee(tender, currency) {
  const cur = currency || tender?.currency || "USD";
  const amount = tender?.loan_fee_amount;
  if (tender?.has_loan_fee && shouldRender(amount) && Number(amount) > 0) {
    return formatCurrencyWithSymbol(amount, cur);
  }
  return "Free";
}

export function formatFeeBlock(tender) {
  if (!tender) return { label: null, value: null };
  const currency = tender.currency || "USD";
  if (tender.type_of_tender === "buy") {
    if (shouldRender(tender.maximum_transfer_fee)) {
      return {
        label: "Transfer Fee",
        value: formatCurrencyWithSymbol(tender.maximum_transfer_fee, currency),
      };
    }
    return { label: "Transfer Fee", value: "Free transfer" };
  }
  if (tender.type_of_tender === "loan") {
    // 2-state via the shared helper: a real amount, else "Free". Serializer
    // exposes has_loan_fee + loan_fee_amount (legacy `loan_fee` not on payload).
    return { label: "Loan Fee", value: formatLoanFee(tender, currency) };
  }
  // Default: market value
  if (tender.market_value_type === "not_important") {
    return { label: "Market Value", value: "Not important" };
  }
  if (tender.market_value_type === "market_value_range") {
    const v = formatCurrencyRange(
      tender.market_value_from,
      tender.market_value_to,
      currency,
    );
    return { label: "Market Value", value: v };
  }
  if (shouldRender(tender.fix_market_value)) {
    return {
      label: "Market Value",
      value: formatCurrencyWithSymbol(tender.fix_market_value, currency),
    };
  }
  return { label: "Market Value", value: null };
}

// Division line: "Country - Division | 1st Tier" with all parts optional.
export function formatDivisionLine(tender) {
  if (!tender) return null;
  const country = tender.division_country?.name;
  const division = tender.division;
  const tier = tender.tier;
  let tierLabel = null;
  if (tier === 1) tierLabel = "1st Tier";
  else if (tier === 2) tierLabel = "2nd Tier";
  else if (tier === 3) tierLabel = "3rd Tier";
  else if (shouldRender(tier)) tierLabel = `${tier}th Tier`;
  const left = [country, division].filter(shouldRender).join(" - ");
  if (left && tierLabel) return `${left} | ${tierLabel}`;
  return left || tierLabel || null;
}

// Resolve a tender's attached-document URL across the field shapes the
// backend has used over time. The detail/list serializers may expose a
// presigned `document_url` (string), a legacy `document` (string), or a
// `documents` array of either strings or { url|document_url|file|path }
// objects — mirroring the normalisation already done in EditTender.js.
// Returns a string URL or null.
export function getDocumentUrl(tender) {
  if (!tender) return null;
  const pickFromObject = (o) =>
    o && typeof o === "object"
      ? o.url || o.document_url || o.file || o.path || null
      : null;
  const candidates = [
    tender.document_url,
    tender.document,
    Array.isArray(tender.documents) && tender.documents.length > 0
      ? tender.documents[0]
      : null,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && shouldRender(c)) return c.trim();
    const fromObj = pickFromObject(c);
    if (typeof fromObj === "string" && shouldRender(fromObj))
      return fromObj.trim();
  }
  return null;
}

// True when the tender has at least one downloadable document.
export function hasAttachedDocument(tender) {
  return shouldRender(getDocumentUrl(tender));
}

// ── Request drafts ─────────────────────────────────────────────────────────
// A draft is a request the club alone can see; it carries status 'draft' and,
// when planned, a `scheduled_publish_at` ISO string. These three helpers are
// the SINGLE reading of those two fields, so the card badge, the list row, the
// schedule dialog and the wizard can never disagree about what a draft is or
// how its date reads.

// True when this row is a draft the club has not published yet.
export function isDraftRequest(tender) {
  return (tender?.status || "") === "draft";
}

// The raw planned publish moment as a Date, or null when none is set.
export function scheduledPublishDate(tender) {
  const raw = tender?.scheduled_publish_at;
  if (!shouldRender(raw)) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

// "Aug 15, 09:00" — the one date+time reading used by the Scheduled badge, the
// schedule dialog's confirm button and the card action label. Deliberately
// en-GB short month plus 24 hour clock, matching the platform's other absolute
// dates (formatScoutDateAbsolute, formatDateRange on the requests header).
// The year joins in only when it carries information, i.e. when the moment
// falls outside the current year: the schedule picker accepts dates in
// following years, and "20 Aug, 09:00" alone cannot tell next August from this
// one.
export function formatScheduledPublish(value) {
  const d = value instanceof Date ? value : value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const thisYear = d.getFullYear() === new Date().getFullYear();
  const day = d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(thisYear ? {} : { year: "numeric" }),
  });
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
  return `${day}, ${time}`;
}

// "15 Aug 2026" — the date-only reading of the club requests page. Identical
// to the en-GB format the page's own transfer-window line already prints
// (hooks/clubtender/useClubTenders.js:247-251), so the schedule dialog's hint
// cannot show a different date order than the header two rows above it.
export function formatRequestDate(value) {
  const d = value instanceof Date ? value : value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ── Request archive (archive instead of delete) ──────────────────────────
// The archive IS the terminal set: a club request that ended — closed by the
// club, expired by the inactivity sweep, or concluded in a deal — lives on
// under the Archive tab for twelve months instead of being deleted. There is
// deliberately NO separate 'archived' status: this list mirrors the backend's
// ARCHIVE_STATUSES, the exact set the lifecycle counter already counts.
export const ARCHIVE_STATUSES = ["expired", "deal"];

// True when this row has ended and lives in the archive. Single reading for
// the badge, the card actions, the list row and the kebab, so they can never
// disagree about what "archived" means.
export function isArchivedRequest(tender) {
  return ARCHIVE_STATUSES.includes(tender?.status || "");
}

// The moment the row entered the archive as a Date, or null when the backend
// has not sent `archived_at` (rows terminal since before the field existed
// are backfilled server-side, so null stays the rare exception).
export function archivedDate(tender) {
  const raw = tender?.archived_at;
  if (!shouldRender(raw)) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}
